import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Users, UserRound, Map, Building2, Gauge } from "lucide-react";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { TabsHeader } from "@/components/ui/tabs-header";
import { PeriodFilter } from "@/components/ui/period-filter";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { useSidebar } from "@/contexts/SidebarContext";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/hooks/useAuth";
import { AdvisorMembersTab } from "@/lib/dashboard/AdvisorMembersTab";
import { AgencyOverviewTab } from "@/lib/dashboard/AgencyOverviewTab";
import { ClientsTab } from "@/lib/dashboard/clients/ClientsTab";
import { TripsTab } from "@/lib/dashboard/trips/TripsTab";
import { AgenciesTab } from "@/lib/dashboard/agencies/AgenciesTab";
import { PERIOD_OPTIONS } from "@/lib/dashboard/periodOptions";
import { AgencyDashboardPeriod } from "@/integrations/supabase/rpc-types";

const AgencyOverview = () => {
  const { t } = useI18n();
  const { isSidebarCollapsed } = useSidebar();
  const { userRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<AgencyDashboardPeriod>("30d");

  const handleTabChange = (value: string) => setSearchParams({ tab: value });

  // Salto dalla scheda di un consulente a quella di un suo cliente: cambia tab e porta con
  // se' la chiave, che ClientsTab consuma una volta sola. Passa dallo stato e non dall'URL
  // perche' e' un'apertura, non una posizione a cui tornare col back del browser.
  const [pendingClientKey, setPendingClientKey] = useState<string | null>(null);
  const handleOpenClient = (clientKey: string) => {
    setPendingClientKey(clientKey);
    setSearchParams({ tab: "clients" });
  };

  const showAdvisorsTab = userRole !== "travel_advisor_member" && userRole !== "travel_designer";
  // La tab Clienti c'e' per tutti i ruoli della pagina: cambia solo cosa ci si trova
  // dentro, che e' il perimetro gia' deciso dalla RPC. Il filtro per advisor invece ha
  // senso solo per chi vede il lavoro di piu' persone.
  const canFilterByAdvisor = showAdvisorsTab;
  // Agenzie e indipendenti con la loro aliquota fee: e' una trattativa di Place2Be, la vede
  // e la modifica solo l'amministrazione (le RPC respingono comunque gli altri).
  const showAgenciesTab = userRole === "admin";

  // La panoramica KPI e' la prima tab: stesso periodo delle liste di dettaglio, che sono
  // il passo successivo dopo aver letto i numeri.
  const tabs = [
    { value: "overview", label: t("dashboard.overview.tabs.overview"), icon: Gauge },
    ...(showAdvisorsTab ? [{ value: "advisors", label: t("dashboard.overview.tabs.advisors"), icon: Users }] : []),
    { value: "clients", label: t("dashboard.overview.tabs.clients"), icon: UserRound },
    { value: "trips", label: t("dashboard.overview.tabs.trips"), icon: Map },
    ...(showAgenciesTab ? [{ value: "agencies", label: t("dashboard.overview.tabs.agencies"), icon: Building2 }] : []),
  ];
  const requestedTab = searchParams.get("tab");
  const activeTab = tabs.some((tab) => tab.value === requestedTab) ? requestedTab! : tabs[0].value;

  return (
    <PrivateLayout
      pageTitle={t("dashboard.overview.dataPageTitle")}
      pageDescription={t("dashboard.overview.dataPageDescription")}
      showBackButton={false}
      // Altezza piena: la lista della tab riempie lo spazio rimasto e la paginazione
      // resta in fondo.
      contentClassName="h-full flex flex-col min-h-0 py-2 md:py-4"
    >
      <div className="flex flex-1 flex-col min-h-0 gap-4">
        <TabsHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabs}
          sidebarCollapsed={isSidebarCollapsed}
        />

        <div className="flex flex-1 flex-col min-h-0 w-full px-4 mx-auto gap-4">
          {/* Period selector: le aliquote non hanno un periodo. */}
          <div className={`shrink-0 flex justify-end ${activeTab === "agencies" ? "hidden" : ""}`}>
            <PeriodFilter
              options={PERIOD_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(opt.labelKey),
              }))}
              value={period}
              onChange={(value) => setPeriod(value as AgencyDashboardPeriod)}
              ariaLabel={t("dashboard.overview.periodLabel")}
            />
          </div>

          {/* Radix lascia nel DOM i pannelli inattivi con l'attributo `hidden`: la classe
              `flex` lo scavalcherebbe, da qui data-[state=inactive]:hidden. */}
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="flex flex-1 flex-col min-h-0 w-full"
          >
            {/* A flusso normale, non a tabella: scrolla dentro lo spazio della tab. */}
            <ProfileTabsContent
              value="overview"
              className="flex-1 min-h-0 overflow-y-auto data-[state=inactive]:hidden"
            >
              <AgencyOverviewTab period={period} />
            </ProfileTabsContent>
            {showAdvisorsTab && (
              <ProfileTabsContent
                value="advisors"
                className="flex flex-1 flex-col min-h-0 data-[state=inactive]:hidden"
              >
                <AdvisorMembersTab period={period} onOpenClient={handleOpenClient} />
              </ProfileTabsContent>
            )}
            <ProfileTabsContent
              value="clients"
              className="flex flex-1 flex-col min-h-0 data-[state=inactive]:hidden"
            >
              <ClientsTab
                period={period}
                canFilterByAdvisor={canFilterByAdvisor}
                openClientKey={pendingClientKey}
                onOpenedClient={() => setPendingClientKey(null)}
              />
            </ProfileTabsContent>
            <ProfileTabsContent
              value="trips"
              className="flex flex-1 flex-col min-h-0 data-[state=inactive]:hidden"
            >
              <TripsTab period={period} canFilterByAdvisor={canFilterByAdvisor} />
            </ProfileTabsContent>
            {showAgenciesTab && (
              <ProfileTabsContent
                value="agencies"
                className="flex flex-1 flex-col min-h-0 data-[state=inactive]:hidden"
              >
                <AgenciesTab />
              </ProfileTabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </PrivateLayout>
  );
};

export default AgencyOverview;
